// src/pages/Profile.jsx
import React, {
  useContext,
  useEffect,
  useState,
  useCallback,
  useMemo,
  useRef,
} from "react";
import { Button, Container, ListGroup, Alert, Spinner } from "react-bootstrap";
import {
  getAuth,
  onAuthStateChanged,
  deleteUser,
  reauthenticateWithCredential,
  EmailAuthProvider,
  reauthenticateWithPopup,
  GoogleAuthProvider,
  signOut,
} from "firebase/auth";
import { Link, useNavigate } from "react-router-dom";

import { usePro } from "../contexts/ProContext";
import { UserContext } from "../contexts/UserContext";
import { useOwnerQa } from "../contexts/OwnerQaContext";
import PuzzleTrainingQaPanel from "../components/PuzzleTrainingQaPanel";
import {
  BASE_URL,
  getUserSuccesses,
  getTotalUserFailures,
  getTotalUserHints,
} from "../integrations/APIClient";
import { formatSubscriptionDate } from "../utils/subscription";

export default function Profile() {
  const navigate = useNavigate();
  const qa = useOwnerQa();
  const {
    user: ctxUser,
    firebaseUser: ctxFirebaseUser,
    loading: ctxAuthLoading,
    refresh: refreshUser,
  } = useContext(UserContext);
  const { isPro, subscription } = usePro();
  const [fbUser, setFbUser] = useState(ctxFirebaseUser || null);
  const [authLoading, setAuthLoading] = useState(ctxAuthLoading);

  // local profile we actually render (ctx or fetched or fallback)
  const [profile, setProfile] = useState(ctxUser || null);
  const [profileLoading, setProfileLoading] = useState(false);

  const [loading, setLoading] = useState(false); // delete button spinner
  const [refreshingPlan, setRefreshingPlan] = useState(false);
  const [signingOut, setSigningOut] = useState(false);
  const [statsLoading, setStatsLoading] = useState(true);
  const [stats, setStats] = useState({ successes: 0, failures: 0, hints: 0 });
  const [msg, setMsg] = useState(null);
  const [err, setErr] = useState(null);

  const isMounted = useRef(true);
  useEffect(() => {
    isMounted.current = true;
    return () => {
      isMounted.current = false;
    };
  }, []);

  // In dev, use CRA proxy by going relative; in prod, BASE_URL is same-origin
  const apiUserUrl = useMemo(
    () =>
      process.env.NODE_ENV === "development"
        ? "/api/user"
        : `${BASE_URL}/api/user`,
    []
  );

  // ---- Firebase auth subscription ----
  useEffect(() => {
    const auth = getAuth();
    const unsub = onAuthStateChanged(auth, (u) => {
      setFbUser(u || null);
      setAuthLoading(false);
    });
    return () => unsub();
  }, []);

  useEffect(() => {
    setFbUser(ctxFirebaseUser || null);
    setAuthLoading(ctxAuthLoading);
  }, [ctxAuthLoading, ctxFirebaseUser]);

  useEffect(() => {
    let cancelled = false;
    if (!ctxFirebaseUser && !getAuth().currentUser) {
      setStatsLoading(false);
      return undefined;
    }
    setStatsLoading(true);
    Promise.all([
      getUserSuccesses(),
      getTotalUserFailures(),
      getTotalUserHints(),
    ])
      .then(([successData, failureData, hintData]) => {
        if (cancelled) return;
        setStats({
          successes: Number(successData?.totalSuccessCount || 0),
          failures: Number(failureData?.totalFailureCount || 0),
          hints: Number(hintData?.totalHintsCount || 0),
        });
      })
      .catch(() => {
        if (!cancelled) setStats({ successes: 0, failures: 0, hints: 0 });
      })
      .finally(() => {
        if (!cancelled) setStatsLoading(false);
      });
    return () => { cancelled = true; };
  }, [ctxFirebaseUser]);

  // If context user arrives later, adopt it
  useEffect(() => {
    if (ctxUser) setProfile(ctxUser);
  }, [ctxUser]);

  const safeRead = useCallback(async (res) => {
    const ct = res.headers.get("content-type") || "";
    try {
      if (ct.includes("application/json")) {
        const j = await res.json();
        return typeof j === "string" ? j : JSON.stringify(j);
      }
      return await res.text();
    } catch {
      return "";
    }
  }, []);

  const minimalProfileFromFirebase = useCallback((u) => {
    if (!u) return null;
    return {
      email: u.email || "—",
      username: u.displayName || (u.email ? u.email.split("@")[0] : "—"),
      fullName: u.displayName || "—",
      rating: "—",
      category_rating: null,
    };
  }, []);

  // If we have a Firebase user but no club profile, try to fetch it
  const fetchProfile = useCallback(async () => {
    if (!fbUser || profile) return;

    setProfileLoading(true);
    const controller = new AbortController();

    try {
      const auth = getAuth();
      const current = auth.currentUser;
      if (!current) {
        if (isMounted.current) setProfile(minimalProfileFromFirebase(fbUser));
        return;
      }

      const idToken = await current.getIdToken(true);
      const res = await fetch(apiUserUrl, {
        method: "GET",
        headers: { Authorization: `Bearer ${idToken}` },
        signal: controller.signal,
      });

      if (res.ok) {
        const data = await res.json();
        if (isMounted.current) setProfile(data);
      } else if (res.status === 404) {
        // No club doc yet — fall back to a minimal profile from Firebase
        if (isMounted.current) setProfile(minimalProfileFromFirebase(fbUser));
      } else {
        const text = await safeRead(res);
        if (isMounted.current) {
          setErr(
            `Failed to load profile (${res.status}). ${text || ""}`.trim()
          );
          setProfile(minimalProfileFromFirebase(fbUser));
        }
      }
    } catch (e) {
      if (e.name === "AbortError") return;
      if (isMounted.current) {
        setErr(e?.message || "Error loading profile.");
        setProfile(minimalProfileFromFirebase(fbUser));
      }
    } finally {
      if (isMounted.current) setProfileLoading(false);
    }

    return () => controller.abort();
  }, [apiUserUrl, fbUser, profile, minimalProfileFromFirebase, safeRead]);

  useEffect(() => {
    fetchProfile();
  }, [fetchProfile]);

  // ----- Delete account flow -----
  const getFreshAuthHeader = async () => {
    const auth = getAuth();
    const currentUser = auth.currentUser;
    if (!currentUser) throw new Error("No authenticated Firebase user");
    const idToken = await currentUser.getIdToken(true);
    return { Authorization: `Bearer ${idToken}` };
  };

  const reauthenticate = async () => {
    const auth = getAuth();
    const currentUser = auth.currentUser;
    if (!currentUser) throw new Error("No authenticated Firebase user");

    // Prefer the last provider if multiple
    const providerId =
      currentUser.providerData?.[currentUser.providerData.length - 1]
        ?.providerId || "password";

    if (providerId === "password") {
      const email = currentUser.email || "";
      const pw = window.prompt(
        `For security, please re-enter your password for ${email}:`
      );
      if (!pw) throw new Error("Reauthentication cancelled");
      const cred = EmailAuthProvider.credential(email, pw);
      await reauthenticateWithCredential(currentUser, cred);
      return;
    }

    if (providerId === "google.com") {
      const provider = new GoogleAuthProvider();
      await reauthenticateWithPopup(auth, provider);
      return;
    }

    throw new Error("Recent login required. Please sign out/in and retry.");
  };

  const handleDelete = async () => {
    if (loading) return;
    setMsg(null);
    setErr(null);
    if (!window.confirm("Delete your account permanently?")) return;

    try {
      setLoading(true);

      const auth = getAuth();
      const currentUser = auth.currentUser;
      if (!currentUser) throw new Error("No authenticated Firebase user");

      // Backend delete (accept any 2xx, retry on 401/403)
      let headers = await getFreshAuthHeader();
      let res = await fetch(apiUserUrl, { method: "DELETE", headers });
      if (res.status === 401 || res.status === 403) {
        headers = await getFreshAuthHeader();
        res = await fetch(apiUserUrl, { method: "DELETE", headers });
      }
      if (!res.ok) {
        const body = await safeRead(res);
        throw new Error(
          `Backend refused deletion (${res.status}). ${body || ""}`.trim()
        );
      }

      // Firebase delete (reauth if needed)
      try {
        await deleteUser(currentUser);
      } catch (e) {
        if (e?.code === "auth/requires-recent-login") {
          await reauthenticate();
          await deleteUser(getAuth().currentUser);
        } else {
          throw e;
        }
      }

      setMsg("Your account has been deleted successfully.");
      setTimeout(() => (window.location.href = "/"), 900);
    } catch (e) {
      console.error("❌ Delete account failed:", e);
      setErr(e?.message || "Error deleting account. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const refreshSubscriptionStatus = async () => {
    try {
      setRefreshingPlan(true);
      setMsg(null);
      setErr(null);
      await refreshUser();
      setMsg("Subscription status refreshed from your backend profile.");
    } catch (e) {
      setErr(e?.message || "Error refreshing subscription status.");
    } finally {
      setRefreshingPlan(false);
    }
  };

  const subscriptionSource =
    subscription?.provider || subscription?.source || "backend";
  const subscriptionExpiresLabel = formatSubscriptionDate(subscription?.expiresAt);

  const handleLogout = async (switchAccount = false) => {
    if (signingOut) return;
    setSigningOut(true);
    setErr(null);
    try {
      await signOut(getAuth());
      if (switchAccount) {
        navigate("/login", {
          replace: true,
          state: { from: { pathname: "/" } },
        });
      } else {
        navigate("/", { replace: true });
      }
    } catch (e) {
      setErr(e?.message || "Could not log out. Please try again.");
    } finally {
      setSigningOut(false);
    }
  };

  // ---------- Rendering ----------
  if (authLoading || (!profile && profileLoading)) {
    return (
      <Container className="mt-4 d-flex align-items-center" style={{ gap: 8 }}>
        <Spinner animation="border" size="sm" />{" "}
        <span>Loading your profile…</span>
      </Container>
    );
  }

  if (!fbUser && !profile) {
    return (
      <Container className="mt-4">
        <Alert variant="info">Please log in to see your profile.</Alert>
      </Container>
    );
  }

  return (
    <Container className="mt-4" style={{ maxWidth: 840 }}>
      {/* local styles for this page */}
      <style>{`
        .profile-card {
          background: #0f0f0f;
          border: 1px solid #ff7a18;
          border-radius: 14px;
          box-shadow: 0 14px 28px rgba(0,0,0,.35);
          overflow: hidden;
        }
        .profile-header {
          text-align: center;
          padding: 18px 16px 8px;
          background: radial-gradient(1200px 400px at 50% -20%, rgba(255,122,24,.18), transparent 60%);
        }
        .profile-header h2 {
          margin: 0;
          font-weight: 800;
          color: #f0f0f0;
          letter-spacing: .2px;
        }
        .profile-plan-pill {
          display: inline-flex;
          align-items: center;
          margin-top: 12px;
          padding: 8px 12px;
          border-radius: 999px;
          background: ${isPro ? "rgba(255,215,97,.14)" : "rgba(255,255,255,.08)"};
          color: ${isPro ? "#ffd761" : "#f5e8d2"};
          font-weight: 800;
        }
        .profile-body {
          padding: 12px 16px 18px;
        }
        .profile-list .list-group-item {
          background: transparent;
          border-color: rgba(255,255,255,.08) !important;
          color: #e6e6e6;
          padding: 12px 14px;
        }
        .profile-list strong {
          color: #f8f8f8;
        }
        .profile-cta {
          display: flex;
          flex-wrap: wrap;
          gap: 10px;
          align-items: center;
          padding: 14px 16px 18px;
          border-top: 1px solid rgba(255,255,255,.08);
          background: linear-gradient(180deg, rgba(255,122,24,.06), rgba(255,122,24,.02));
        }
        .profile-cta .btn {
          border-radius: 10px;
          padding: 10px 14px;
          font-weight: 700;
        }
        .profile-dashboard {
          display:grid;
          grid-template-columns:repeat(3,minmax(0,1fr));
          gap:10px;
          padding:0 16px 16px;
        }
        .profile-stat {
          border:1px solid rgba(255,255,255,.10);
          border-radius:14px;
          padding:12px;
          background:rgba(255,255,255,.04);
          text-align:center;
        }
        .profile-stat strong {
          display:block;
          color:#ffd761;
          font-size:1.45rem;
        }
        .profile-stat span {
          color:#d4d4d4;
          font-size:.82rem;
        }
        .profile-links {
          display:flex;
          flex-wrap:wrap;
          gap:8px;
          padding:0 16px 16px;
        }
        @media (max-width: 640px) {
          .profile-dashboard { grid-template-columns:1fr 1fr 1fr; gap:6px; padding:0 10px 12px; }
          .profile-stat { padding:9px 5px; }
          .profile-stat strong { font-size:1.15rem; }
          .profile-stat span { font-size:.70rem; }
          .profile-cta { align-items:stretch; }
          .profile-cta .btn { flex:1 1 100%; }
        }
        .btn-danger.pretty {
          background: linear-gradient(135deg, #8b1d22, #b02227);
          border: 1px solid rgba(255,255,255,.12);
          box-shadow: 0 8px 18px rgba(176,34,39,.35);
        }
        .btn-danger.pretty:disabled {
          opacity: .7;
        }
      `}</style>

      {msg && (
        <Alert
          variant="success"
          onClose={() => setMsg(null)}
          dismissible
          className="mb-3"
        >
          {msg}
        </Alert>
      )}
      {err && (
        <Alert
          variant="danger"
          onClose={() => setErr(null)}
          dismissible
          className="mb-3"
        >
          {err}
        </Alert>
      )}

      <div className="profile-card">
        <div className="profile-header">
          <h2>Your Profile</h2>
          <div className="profile-plan-pill">
            {isPro ? "Pro active" : "Free plan"}
          </div>
        </div>

        <div className="profile-body">
          <ListGroup variant="flush" className="profile-list mb-0">
            <ListGroup.Item>
              <strong>Email:</strong> {profile?.email ?? fbUser?.email ?? "—"}
            </ListGroup.Item>
            <ListGroup.Item>
              <strong>Username:</strong>{" "}
              {profile?.username ??
                profile?.fullName ??
                fbUser?.displayName ??
                (fbUser?.email ? fbUser.email.split("@")[0] : "—")}
            </ListGroup.Item>
            <ListGroup.Item>
              <strong>Full Name:</strong>{" "}
              {profile?.fullName ?? fbUser?.displayName ?? "—"}
            </ListGroup.Item>
            <ListGroup.Item>
              <strong>Rating:</strong> {profile?.rating ?? "—"}
            </ListGroup.Item>
            <ListGroup.Item>
              <strong>Subscription:</strong> {isPro ? "Pro" : "Free"}
              <div style={{ marginTop: 6 }}>
                Source: {subscriptionSource}
              </div>
              <div style={{ marginTop: 4 }}>
                {subscriptionExpiresLabel
                  ? `Valid until ${subscriptionExpiresLabel}`
                  : isPro
                    ? "Active with no expiry date exposed in profile."
                    : "No active Pro entitlement found yet."}
              </div>
            </ListGroup.Item>
            <ListGroup.Item>
              <strong>Category Ratings:</strong>
              <ul className="mb-0" style={{ paddingLeft: 18 }}>
                {profile?.category_rating
                  ? Object.entries(profile.category_rating).map(
                      ([cat, val]) => (
                        <li key={cat}>
                          {cat}: {val}
                        </li>
                      )
                    )
                  : "—"}
              </ul>
            </ListGroup.Item>
          </ListGroup>
        </div>

        <div className="profile-dashboard" aria-label="Your chess statistics">
          <div className="profile-stat">
            <strong>{statsLoading ? "…" : stats.successes}</strong>
            <span>Correct puzzles</span>
          </div>
          <div className="profile-stat">
            <strong>{statsLoading ? "…" : stats.failures}</strong>
            <span>Failed attempts</span>
          </div>
          <div className="profile-stat">
            <strong>{statsLoading ? "…" : stats.hints}</strong>
            <span>Hints used</span>
          </div>
        </div>

        {qa.isOwner && (
          <div style={{ padding: "0 16px" }}>
            <PuzzleTrainingQaPanel />
          </div>
        )}

        <div className="profile-links">
          <Button as={Link} to="/stats" variant="outline-light">Full statistics</Button>
          <Button as={Link} to="/entrytraining" variant="outline-warning">Training</Button>
          <Button as={Link} to="/hood-ai-alpha" variant="outline-warning">Hood AI Alpha</Button>
          <Button as={Link} to="/analysis" variant="outline-warning">AI Analysis</Button>
          {qa.isOwner && <Button as={Link} to="/hood-ai-beta" variant="info">Hood AI Beta</Button>}
        </div>

        <div className="profile-cta">
          <Button
            as={Link}
            to="/checkout"
            variant="warning"
            className="d-flex align-items-center gap-2"
          >
            {isPro ? "Manage subscription" : "Subscribe to Pro"}
          </Button>
          <Button
            variant="secondary"
            onClick={refreshSubscriptionStatus}
            disabled={refreshingPlan}
            className="d-flex align-items-center gap-2"
          >
            {refreshingPlan && <Spinner animation="border" size="sm" />}
            Refresh status
          </Button>
          <Button
            variant="dark"
            onClick={() => handleLogout(false)}
            disabled={signingOut}
          >
            {signingOut ? "Logging out…" : "Log out"}
          </Button>
          <Button
            variant="outline-light"
            onClick={() => handleLogout(true)}
            disabled={signingOut}
          >
            Use another account
          </Button>
          <Button
            variant="danger"
            onClick={handleDelete}
            disabled={loading || !fbUser}
            className="pretty d-flex align-items-center gap-2"
          >
            {loading && <Spinner animation="border" size="sm" />}
            Delete Account
          </Button>
        </div>
      </div>
    </Container>
  );
}
